import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Select,
  DatePicker,
  Tag,
  Table,
  Space,
  Typography,
  Tooltip,
  Alert,
  message,
} from "antd";
import {
  PlusOutlined,
  CopyOutlined,
  DeleteOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { Button, Modal } from "../../../components/Common";
import SearchSelect from "../../../components/SearchSelect";
import { useMutations } from "../../../hooks/useApiMutation";
import { useConfirm } from "../../../hooks/useConfirm";

const { Text, Paragraph } = Typography;

const KeysTab = ({ agent }) => {
  const { mutate } = useMutations();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;
  const { confirmAction } = useConfirm();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealed, setRevealed] = useState(null);
  const [form] = Form.useForm();
  // T-2892: a global agent has no owning company, so the key must name the subscription
  // (client) it bills - the server no longer guesses one. Company agents keep billing their
  // own company's active subscription.
  const isGlobal = agent.companyId == null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await mutateRef.current("fetchAgentKeys", {
        method: "get",
        endpoint: `/v1/agents/${agent.agentId}/keys`,
      });
      setKeys(data?.data || []);
    } catch {
      setKeys([]);
    } finally {
      setLoading(false);
    }
  }, [agent.agentId]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    form.resetFields();
    form.setFieldsValue({ environment: "live", scopes: "chat" });
    setCreateOpen(true);
  };

  const onCreate = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null,
      };
      const { data, error } = await mutate("createAgentKey", {
        method: "post",
        endpoint: `/v1/agents/${agent.agentId}/keys`,
        payload,
      });
      // The server message (e.g. "choose the subscription") is already shown; keep the form open.
      if (error) return;
      setCreateOpen(false);
      const raw = data?.data?.rawKey;
      const keyInfo = data?.data?.key;
      if (raw) {
        setRevealed({ raw, ...keyInfo });
        setRevealOpen(true);
      }
      load();
    } catch (err) {
      if (err?.errorFields) return;
      message.error("Failed to create key");
    }
  };

  const copyToClipboard = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success("Copied");
    } catch {
      message.error("Copy failed");
    }
  };

  const columns = [
    {
      title: "Prefix",
      dataIndex: "keyPrefix",
      key: "keyPrefix",
      render: (p) => (
        <Space>
          <KeyOutlined />
          <Text code>{p}…</Text>
        </Space>
      ),
    },
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: "Bills",
      key: "subscription",
      render: (_, r) =>
        r.subscriptionClient ? (
          <Tooltip title={`Subscription #${r.subscriptionId}`}>
            <span>{r.subscriptionClient}</span>
          </Tooltip>
        ) : (
          <Text type="secondary">#{r.subscriptionId}</Text>
        ),
      responsive: ["md"],
    },
    {
      title: "Env",
      dataIndex: "environment",
      key: "environment",
      render: (e) => (
        <Tag color={e === "live" ? "green" : "gold"}>{e}</Tag>
      ),
    },
    {
      title: "Scopes",
      dataIndex: "scopes",
      key: "scopes",
      render: (s) =>
        (s || "").split(",").filter(Boolean).map((x) => (
          <Tag key={x}>{x.trim()}</Tag>
        )),
      responsive: ["md"],
    },
    {
      title: "Last used",
      dataIndex: "lastUsedAt",
      key: "lastUsedAt",
      render: (v) =>
        v ? (
          dayjs(v).fromNow?.() || new Date(v).toLocaleString()
        ) : (
          <Text type="secondary">Never</Text>
        ),
      responsive: ["lg"],
    },
    {
      title: "Status",
      key: "status",
      render: (_, r) => {
        if (r.revokedAt) return <Tag color="error">Revoked</Tag>;
        if (r.expiresAt && dayjs(r.expiresAt).isBefore(dayjs()))
          return <Tag color="warning">Expired</Tag>;
        return r.active ? (
          <Tag color="success">Active</Tag>
        ) : (
          <Tag>Inactive</Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, r) => (
        <Space>
          <Tooltip title="Copy prefix">
            <Button
              variant="ghost"
              icon={<CopyOutlined />}
              aria-label={`Copy prefix ${r.keyPrefix}`}
              onClick={() => copyToClipboard(r.keyPrefix)}
            />
          </Tooltip>
          {r.active && !r.revokedAt && (
            <Tooltip title="Revoke">
              <Button
                variant="ghost"
                danger
                icon={<DeleteOutlined />}
                aria-label={`Revoke ${r.keyPrefix}`}
                onClick={() =>
                  confirmAction({
                    key: "revokeAgentKey",
                    method: "delete",
                    endpoint: `/v1/agents/${agent.agentId}/keys/${r.agentApiKeyId}`,
                    title: "Revoke key",
                    content: `Revoke ${r.keyPrefix}…? This cannot be undone.`,
                    okText: "Revoke",
                    okType: "danger",
                    onSuccess: load,
                  })
                }
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <div>
          <Text strong style={{ fontSize: 16 }}>
            Agent keys
          </Text>
          <div>
            <Text type="secondary">
              Issue <Text code>kb_ak_...</Text> keys consumers use on{" "}
              <Text code>POST /api/v1/chat</Text>. The raw key is shown once —
              store it safely.
            </Text>
          </div>
        </div>
        <Button
          variant="primary"
          icon={<PlusOutlined />}
          onClick={openCreate}
          disabled={agent.status !== "Published"}
        >
          Issue key
        </Button>
      </Space>

      {agent.status !== "Published" && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Publish the agent before issuing keys."
        />
      )}

      <Table
        rowKey="agentApiKeyId"
        loading={loading}
        dataSource={keys}
        columns={columns}
        pagination={false}
      />

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onConfirm={onCreate}
        title="Issue agent key"
        confirmText="Create"
        width={520}
      >
        <Form form={form} layout="vertical">
          {isGlobal && (
            <Form.Item
              name="subscriptionId"
              label="Subscription (client billed)"
              tooltip="This agent is global: every call made with this key is billed to the chosen subscription. The caller's X-Api-Key must belong to the same subscription."
              rules={[{ required: true, message: "Choose the subscription this key bills" }]}
            >
              <SearchSelect
                fetchKey="searchSubscriptions"
                endpoint="/subscription/search"
                labelKey="description"
                valueKey="subscriptionId"
                placeholder="Select subscription"
              />
            </Form.Item>
          )}
          <Form.Item name="label" label="Label">
            <Input placeholder="Production mobile app" />
          </Form.Item>
          <Form.Item name="environment" label="Environment">
            <Select>
              <Select.Option value="live">Live (billable)</Select.Option>
              <Select.Option value="test">Test (sandbox)</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="scopes"
            label="Scopes"
            tooltip="Comma-separated. Only 'chat' is enforced today."
          >
            <Input placeholder="chat" />
          </Form.Item>
          <Form.Item name="expiresAt" label="Expires at">
            <DatePicker
              showTime
              style={{ width: "100%" }}
              disabledDate={(d) => d && d.isBefore(dayjs(), "day")}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={revealOpen}
        onClose={() => {
          setRevealOpen(false);
          setRevealed(null);
        }}
        title="Copy your key — shown only once"
        confirmText="I've stored it safely"
        onConfirm={() => {
          setRevealOpen(false);
          setRevealed(null);
        }}
        width={560}
      >
        <Alert
          type="warning"
          showIcon
          message="This is the only time you'll see this key. Store it in your secrets manager now."
          style={{ marginBottom: 16 }}
        />
        <Paragraph
          copyable={{ text: revealed?.raw, tooltips: ["Copy", "Copied"] }}
          style={{
            background: "var(--bg-base)",
            color: "var(--accent-blue)",
            border: "1px solid var(--border-default)",
            padding: 12,
            borderRadius: 6,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            wordBreak: "break-all",
          }}
        >
          {revealed?.raw}
        </Paragraph>
        <Space direction="vertical">
          <Text type="secondary">
            Use it as <Text code>Authorization: Bearer {revealed?.raw}</Text>
          </Text>
          {revealed?.environment === "test" && (
            <Text type="secondary">
              This is a <Tag color="gold">test</Tag> key — usage is logged but
              not billed.
            </Text>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default KeysTab;
